"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import Link from "next/link";

import {
  ButtonComponent,
  CheckboxComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
  RadioButtonComponent,
  SecureDataComponent,
  SnackbarComponent,
  TextFieldComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  CKYC_PHONE_SCHEMA,
  CKYC_SCHEMA,
  ConsentType,
  GET_PRODUCT_APPLICATION,
  getPersistentDeviceId,
  MOENGAGE_EVENTS,
  PAN_SCHEMA,
  PAN_TYPES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import { usePermissionStatus } from "@/lib/hooks/consent.hooks";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useOtpTimer from "@/lib/hooks/otpTimerHandler";
import { useGetApplicationQuery, useVerifyPanMutation } from "@/lib/redux";
import {
  useAcceptPanMutation,
  useConsentCreateMutation,
  useFlushPanMutation,
  useGetApplicationStageConfigQuery,
  useGetCustomerQuery,
  useInitiateCkycMutation,
  useSkipCkycMutation,
  useValidateCkycMutation,
} from "@/lib/redux/apis/los-api";
import moengage from "@moengage/web-sdk";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import InvalidPhoneModal from "./modals/InvalidPhone";
import OtpModal from "./modals/OtpModal";
import { checkForRpl, checkForRplProductRoute } from "@/lib/utils/rpl.utils";

const ConsentCheckbox = ({ formik, t, isMobile, customerData }) => {
  return (
    <Stack gap={2}>
      <CheckboxComponent
        onChange={(e) => {
          formik.setFieldValue("consent", e.target.checked);
        }}
        checked={formik?.values?.consent}
        name="consent"
        label={
          <Typography
            fontSize={isMobile ? "0.7rem" : "0.8rem"}
            color={WEB_COLORS.disabledColor}
          >
            {t("cersai")}
          </Typography>
        }
      />
      {formik?.errors?.consent && formik.submitCount > 0 && (
        <FormHelperText sx={{ color: "#d32f2f" }}>
          {formik?.errors?.consent}
        </FormHelperText>
      )}

      {!customerData?.data?.pan && (
        <Typography
          fontWeight={500}
          fontSize={"0.8rem"}
          color={WEB_COLORS.fadeColor}
        >
          {"Don't have your PAN handy?"}{" "}
          <Link
            className="doc-link"
            target="_blank"
            href="https://eportal.incometax.gov.in/iec/foservices/#/login"
          >
            Retrieve my PAN
          </Link>
        </Typography>
      )}
    </Stack>
  );
};

function IdentityDetailsScreen({ product }) {
  //HOOKS
  const theme = useTheme();
  const router = useRouter();
  const { setActions } = useFooter();
  const t = useTranslations("IdentityDetails");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showPanEditModal, setShowPanEditModal] = useState(false);
  const { notification, location, camera } = usePermissionStatus();
  const [showInvalidPhoneModal, setShowInvalidPhoneModal] = useState(false);
  const { timeLeft, setTimeLeft, startTimer, resetTimer } = useOtpTimer(90);

  //API HOOKS
  const {
    data: applicationData,
    error: applicationFetchError,
    isFetching: fetchingApplications,
    isLoading: isLoadingApplications,
  } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const {
    data: customerData,
    isLoading: fetchingCustomerData,
    isError: isErrorCustomer,
  } = useGetCustomerQuery("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const {
    data: appStageConfigData,
    isLoading: isLoadingAppStageConfig,
    error: appStageConfigError,
    refetch: refetchAppStageConfig,
  } = useGetApplicationStageConfigQuery(
    {
      appId: productApplication?.appId,
      stage: productApplication?.stage,
    },
    {
      skip: !productApplication?.appId || !productApplication?.stage,
      refetchOnMountOrArgChange: true,
    },
  );

  const isRPL = productApplication
    ? checkForRpl(productApplication)
    : checkForRplProductRoute(product);

  const triggerEvent = useCallback(
    (eventName) => {
      sendEventAnalytics({
        ...productApplication,
        ...customerData?.data,
        eventName,
      });
    },
    [productApplication, customerData?.data, sendEventAnalytics],
  );

  const [
    verifyPan,
    {
      data: verifyPanData,
      isLoading: verifyingPan,
      isSuccess: panVerified,
      error: verifyPanError,
      isUninitialized: isUninitializedVerifyPan,
      reset: resetVerifyPan,
    },
  ] = useVerifyPanMutation();

  const [
    flushPan,
    {
      isSuccess: isFlushingPan,
      isFetching: flushingPan,
      data: flushPanData,
      error: flushPanError,
    },
  ] = useFlushPanMutation();
  const [
    acceptPan,
    {
      isFetching: acceptingPan,
      data: acceptPanData,
      error: acceptPanError,
      isSuccess: panAccepted,
    },
  ] = useAcceptPanMutation();
  const [createConsent, { isLoading: consentDataLoading }] =
    useConsentCreateMutation();
  const [
    initiateCkyc,
    {
      data: ckycData,
      isLoading: verifyingCkyc,
      isSuccess: ckycSuccess,
      error: verifyCkycError,
    },
  ] = useInitiateCkycMutation();
  const [
    validateCkyc,
    {
      data: validateCkycData,
      error: validateCkycError,
      isLoading: validatingCkyc,
      reset: resetValidateCkyc,
    },
  ] = useValidateCkycMutation();
  // Example usage: Call resetValidateCkyc() wherever you want to reset the validateCkyc API state
  const [
    skipCkyc,
    { data: skipCkycData, error: skipCkycError, isLoading: skippingCkyc },
  ] = useSkipCkycMutation();
  /*************************************ERROR HANDLING*****************************/
  const isError =
    applicationFetchError ||
    verifyCkycError ||
    isErrorCustomer ||
    verifyPanError ||
    flushPanError ||
    acceptPanError ||
    validateCkycError;
  /*************************************EFFECT*****************************/
  useErrorHandling(isError);
  const formik = useFormik({
    validationSchema: PAN_SCHEMA,
    enableReinitialize: true,
    initialValues: {
      pan: customerData?.data?.pan || "",
      consent: false,
      panType: "individual",
    },
    onSubmit: (values) => {
      sendEventAnalytics({
        eventName: WEB_EVENTS.PAN_DETAILS_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
      const body = {
        consent: values?.consent === true ? "Y" : "N",
        pan: values?.pan?.toUpperCase(),
        appId: productApplication?.appId,
      };
      if (!customerData?.data.pan === values.pan || !sessionStorage?.consent) {
        makeConsentRequest(latitude, longitude);
        sessionStorage.consent = true;
      }
      verifyPan(body);
    },
  });
  const ckycFormik = useFormik({
    enableReinitialize: true,
    validationSchema: CKYC_SCHEMA,
    initialValues: {
      firstName: customerData?.data?.firstName || "",
      lastName: customerData?.data?.lastName || "",
      pan: customerData?.data?.pan || "",
      dob: customerData?.data?.dob || formik.values.dob || "",
      gender: customerData?.data?.gender || "",
    },
    onSubmit: (values) => {
      acceptPan({
        appId: productApplication?.appId,
        dob: customerData?.data?.firstName
          ? values?.dob
          : new Date(values?.dob)?.toLocaleDateString("en-GB"),
        pan: values?.pan?.toUpperCase(),
      });
    },
  });
  const ckycPhoneFormik = useFormik({
    enableReinitialize: true,
    validationSchema: CKYC_PHONE_SCHEMA,
    initialValues: { phone: "" },
    onSubmit: (values) => {
      handleCkycInitiate(values.phone);
      triggerEvent(WEB_EVENTS.CKYC_ALTERNATE_MOBILE_PROVIDED);
    },
  });
  const makeConsentRequest = async (lat, lng) => {
    if (lat && lng) {
      const requestData = {
        customerId: productApplication?.customerId,
        appId: productApplication?.appId,
        product_type: productApplication?.product_type,
        latitude: lat?.toString(),
        longitude: lng?.toString(),
        deviceType: "web",
        deviceId: getPersistentDeviceId(),
        deviceName: `${window?.navigator?.userAgent}`,
        type: ConsentType.RUNTIME_PERMISSIONS,
        value: { notification, location, camera },
      };
      createConsent(requestData);
    } else {
      SnackbarComponent({
        error: true,
        errorMessage:
          "Location is required to proceed with consent. Please refresh your browser and allow Location Access",
      });
    }
  };
  const handleFieldChange = (e) => {
    // Only allow uppercase letters and digits
    const rawValue = e.target.value.toUpperCase();
    const filteredValue = rawValue.replace(/[^A-Z0-9]/g, "");
    e.target.value = filteredValue;
    formik.handleChange(e);
  };
  const handleCkycFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      e.target.value = e.target.value.trim();
      ckycPhoneFormik.handleChange(e);
    }
  };
  const backClick = () => {
    router.push(STAGE_BASED_ROUTING(STAGE.APPLY_LOAN));
  };
  const handleCkycInitiate = (phone, isResend) => {
    let mobileNo = phone || customerData?.data?.phone;
    initiateCkyc({
      mobileNo,
      dob: customerData?.data?.dob?.replaceAll("/", "-"),
      appId: productApplication?.appId,
    });
    isResend
      ? triggerEvent(WEB_EVENTS.CKYC_OTP_REGENERATED)
      : triggerEvent(WEB_EVENTS.CKYC_OTP_GENERATED);

    ckycFormik.resetForm();
  };
  const handleCkycValidate = (otp) => {
    triggerEvent(WEB_EVENTS.CKYC_OTP_ENTERED);
    validateCkyc({
      otp,
      ckycRequestId: ckycData?.data?.ckycRequestId,
      appId: productApplication?.appId,
    });
  };
  useEffect(() => {
    const errCode =
      validateCkycError?.data?.error?.code ||
      validateCkycError?.data?.detail?.code;
    if (errCode === "CKYC-0010") triggerEvent(WEB_EVENTS.CKYC_INVALID_OTP);
    else if (errCode === "CKYC-0013" || errCode === "CKYC-0015")
      triggerEvent(WEB_EVENTS.CKYC_OTP_EXPIRED);
  }, [triggerEvent, validateCkycError]);

  const handleResend = () => {
    const errCode =
      validateCkycError?.data?.error?.code ||
      verifyCkycError?.data?.error?.code ||
      validateCkycError?.data?.detail?.code ||
      verifyCkycError?.data?.detail?.code;
    resetTimer();
    if (errCode === "CKYC-0010") {
      triggerEvent(WEB_EVENTS.CKYC_INVALID_OTP);
    }
    if (errCode === "CKYC-0013" || errCode === "CKYC-0015") {
      handleCkycInitiate(ckycPhoneFormik.values.phone, true);
    } else {
      validateCkyc({
        ckycRequestId: ckycData?.data?.ckycRequestId,
        appId: productApplication?.appId,
        retry: "yes",
      });
      triggerEvent(WEB_EVENTS.CKYC_OTP_ENTERED);
      setTimeLeft(Number(validateCkycData?.data?.data?.timeLeft || 90));
      startTimer();
    }
  };
  const handleSkipCkyc = () => {
    skipCkyc({ appId: productApplication?.appId, consent: "Y" });
    triggerEvent(WEB_EVENTS.CKYC_SKIPPED);
  };
  useEffect(() => {
    if (applicationData?.data) {
      const productApplication = GET_PRODUCT_APPLICATION(
        PRODUCT_HANDLER[product],
        applicationData?.data,
      );
      if (productApplication?.stage !== STAGE.CKYC) {
        router.push(STAGE_BASED_ROUTING(productApplication?.stage));
      }
    }
  }, [applicationData?.data]);
  useEffect(() => {
    if (verifyPanError?.data?.message?.toLowerCase()?.includes("invalid")) {
      moengage.trackEvent(MOENGAGE_EVENTS?.PAN_INVALID, {
        phone: customerData?.data?.phone,
      });
      SnackbarComponent({
        error: true,
        errorMessage: verifyPanError?.data?.detail?.message,
      });
    }
  }, [verifyPanError]);

  useEffect(() => {
    if (navigator.geolocation && navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then(function (result) {
          navigator.geolocation.getCurrentPosition((pos) => {
            setLatitude(pos.coords.latitude.toString());
            setLongitude(pos.coords.longitude.toString());
          });
        });
    } else {
      SnackbarComponent({
        error: true,
        errorMessage: "Your browser does not support Geolocation.",
      });
    }
  }, []);

  const ckycOn = useMemo(
    () => Boolean(appStageConfigData?.data?.ckycOn),
    [appStageConfigData],
  );

  useEffect(() => {
    setActions({
      onConfirm:
        productApplication?.stage === STAGE.CKYC
          ? ckycOn
            ? () => handleCkycInitiate(customerData?.data?.phone)
            : handleSkipCkyc
          : !customerData?.data?.panVerified
            ? formik.handleSubmit
            : ckycFormik.handleSubmit,
      confirmBtnLabel:
        productApplication?.stage === STAGE.CKYC
          ? t("proceed")
          : !customerData?.data?.panVerified
            ? t("verifynow")
            : t("proceed"),
      showReject: true,
      disabledConfirmBtn: formik.values.panType === "proprietorship",

      onReject: !isRPL && backClick,
    });
  }, [
    setActions,
    customerData,
    productApplication,
    formik.values.panType,
    isRPL,
    ckycOn,
  ]);

  useEffect(() => {
    if (isFlushingPan) {
      setShowPanEditModal(false);
      resetVerifyPan();
      formik.resetForm();
    }
  }, [isFlushingPan]);

  useEffect(() => {
    if (ckycSuccess) {
      if (ckycData?.data?.code === "CKYC-0000") {
        setShowInvalidPhoneModal(false);
        return;
      }
      setShowOtpModal(true);
      setTimeLeft(Number(ckycData?.data?.additionalData?.timeLeft || 90));
      startTimer();
    }
  }, [ckycSuccess]);

  useEffect(() => {
    if (verifyCkycError?.data?.detail) {
      if (
        (verifyCkycError?.data?.detail?.code === "CKYC-0002" ||
          verifyCkycError?.data?.detail?.code === "CKYC-0007") &&
        !showInvalidPhoneModal
      ) {
        setShowInvalidPhoneModal(true);
      }
    }
  }, [verifyCkycError]);

  useEffect(() => {
    if (panAccepted && productApplication.stage === STAGE.CKYC) {
      triggerEvent(WEB_EVENTS.CKYC_STARTED);
      handleCkycInitiate();
    }
  }, [panAccepted, productApplication, triggerEvent]);

  useEffect(() => {
    if (skipCkycError) {
      if (skipCkycError?.data?.error?.code === "CKYC-STAGE-0003") {
        setShowInvalidPhoneModal(false);
        setShowOtpModal(false);
      }
    }
  }, [skipCkycError]);

  useEffect(() => {
    if (validateCkycError) {
      if (validateCkycError?.data?.additionalData?.attemptLeft === 0) {
        setShowOtpModal(false);
        resetValidateCkyc();
      }
    }
  }, [validateCkycError]);

  /*************************************ACTION FUNCTIONS*****************************/
  /*************************************RENDER*****************************/
  return (
    <>
      <PaperComponent
        elevation={0}
        paperStyle={{
          backgroundColor: "rgba(242, 247, 247, 0.6)",
          border: "1px solid rgba(240, 240, 240, 0.6)",
        }}
      >
        <LoaderComponent
          loadingLabel={
            verifyingPan
              ? "Fetching your PAN details...."
              : validatingCkyc || verifyingCkyc
                ? "Fetching your CKYC records to ensure a smooth loan experience..."
                : ""
          }
          open={
            verifyingPan ||
            fetchingApplications ||
            isLoadingApplications ||
            consentDataLoading ||
            fetchingCustomerData ||
            verifyingCkyc ||
            validatingCkyc ||
            skippingCkyc
          }
        />
        <Typography
          color={WEB_COLORS.primary}
          fontSize={{ xs: 20, sm: 22, md: 24 }}
          fontWeight={700}
        >
          {t(isRPL ? "confirmPan" : "providepan")}
        </Typography>
        <Grid container spacing={2} rowSpacing={1} my={2}>
          <Grid item xs={12} sm={12} md={5}>
            <TextFieldComponent
              disabled={customerData?.data?.pan}
              required
              inputPropsStyle={{ textTransform: "uppercase" }}
              name="pan"
              maxLength={10}
              label={t("pannumber")}
              value={formik.values.pan}
              placeholder={t("pannumber")}
              errorMessage={
                formik?.errors?.pan || verifyPanError?.data?.message
              }
              error={
                Boolean(formik.touched.pan && formik?.errors?.pan) ||
                Boolean(verifyPanError)
              }
              onBlur={formik.handleBlur}
              onChange={(e) => {
                handleFieldChange(e);
                if (!isUninitializedVerifyPan) {
                  resetVerifyPan();
                }
                const panValue = e.target.value?.toUpperCase();
                const panRegex = /^([A-Z]){5}([0-9]){4}([A-Z]){1}$/;
                if (
                  panValue.length === 10 &&
                  !panRegex.test(panValue) &&
                  panValue[3] !== "P"
                ) {
                  moengage.trackEvent(MOENGAGE_EVENTS?.PAN_REGEX_INVALID, {
                    phone: customerData?.data?.phone,
                  });
                }
              }}
            />
          </Grid>
        </Grid>
        {!customerData?.data?.panVerified && !isRPL && (
          <Stack
            my={1}
            spacing={0.5}
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
          >
            <RadioButtonComponent
              name="panType"
              value={formik.values?.panType || ""}
              onChange={(e) => {
                formik.setFieldValue("panType", e.target.value);
              }}
              error={Boolean(formik.touched.panType && formik?.errors?.panType)}
              errorMessage={formik?.errors?.panType}
              radiolabel={"Please select PAN/Constitution type"}
              direction="row"
              menuItems={PAN_TYPES}
            />

            {((formik.errors.panType && formik.values.panType) ||
              (formik.errors.panType && formik.submitCount > 0)) && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                {formik.errors?.panType}
              </FormHelperText>
            )}
          </Stack>
        )}
        {!customerData?.data?.panVerified && !isRPL && (
          <ConsentCheckbox
            formik={formik}
            t={t}
            isMobile={isMobile}
            customerData={customerData}
          />
        )}

        {customerData?.data?.firstName && (
          <Typography mt={3} mb={2}>
            {t("verifydetails")}
          </Typography>
        )}

        <Grid container my={2} spacing={2}>
          {customerData?.data?.dob &&
            (customerData?.data?.panVerified || isRPL) && (
              <Grid item xs={12} sm={6} md={4} order={3}>
                <TextFieldComponent
                  disabled
                  name="dob"
                  label={t("dob")}
                  value={ckycFormik.values.dob}
                />
              </Grid>
            )}
          {customerData?.data?.firstName && (
            <Grid item xs={12} sm={6} md={4} order={1}>
              <TextFieldComponent
                allowCapitalization={true}
                disabled
                name="firstname"
                label={t("firstname")}
                value={ckycFormik.values.firstName}
              />
            </Grid>
          )}
          {customerData?.data?.lastName && (
            <Grid item xs={12} sm={6} md={4} order={2}>
              <TextFieldComponent
                allowCapitalization={true}
                disabled
                name="lastname"
                label={t("lastname")}
                value={ckycFormik.values.lastName}
              />
            </Grid>
          )}
        </Grid>
        {!customerData?.data?.panVerified && isRPL && (
          <ConsentCheckbox
            formik={formik}
            t={t}
            isMobile={isMobile}
            customerData={customerData}
          />
        )}

        {!customerData?.data?.pan && (
          <Stack my={4}>
            <SecureDataComponent rbimsg />
          </Stack>
        )}

        {customerData?.data?.panVerified && (
          <Stack alignItems={"flex-start"} my={2}>
            <Typography
              fontSize={{ xs: 12, sm: 14 }}
              color={WEB_COLORS.textFadeColor}
            >
              {t("information")}
            </Typography>
          </Stack>
        )}

        {!customerData?.data.isPartner && !isRPL && customerData?.data?.pan && (
          <Button
            onClick={() => {
              setShowPanEditModal(true);
            }}
            sx={{ textDecoration: "underline", textTransform: "none", p: 0 }}
          >
            {t("notmyinfo")}
          </Button>
        )}
      </PaperComponent>
      <ModalComponent
        open={showPanEditModal}
        handleModal={setShowPanEditModal}
        title={"Modify PAN"}
      >
        <Stack spacing={2} mx={2}>
          <Typography fontSize={16}>
            You've previously used the PAN below, Are you sure you want to
            update it?
          </Typography>
          <Typography
            align="center"
            fontSize={22}
            fontWeight={600}
            py={2}
            gutterBottom
          >
            {customerData?.data?.pan}
          </Typography>
          <Stack direction={"row"} spacing={4}>
            <ButtonComponent
              variant="outlined"
              type="reset"
              onClick={() => {
                flushPan({ appId: productApplication?.appId });
              }}
            >
              Yes
            </ButtonComponent>
            <ButtonComponent onClick={() => setShowPanEditModal(false)}>
              No
            </ButtonComponent>
          </Stack>
        </Stack>
      </ModalComponent>
      <OtpModal
        loading={validatingCkyc}
        customerData={customerData}
        setShowOtpModal={setShowOtpModal}
        timeLeft={timeLeft}
        showOtpModal={showOtpModal}
        ckycData={ckycData}
        error={validateCkycError || verifyCkycError}
        handleResend={handleResend}
        onSkip={handleSkipCkyc}
        handleCkycValidate={handleCkycValidate}
        isCloseButtonRequired={timeLeft === 0}
      />
      <InvalidPhoneModal
        ckycAttemptsLeft={
          validateCkycError?.metaData?.ckycAttemptsLeft ||
          verifyCkycError?.data?.metaData?.ckycAttemptsLeft
        }
        kycVerified={customerData?.data?.ckycOtpVerified}
        setShowInvalidPhoneModal={setShowInvalidPhoneModal}
        showInvalidPhoneModal={showInvalidPhoneModal}
        onSkip={handleSkipCkyc}
        ckycPhoneFormik={ckycPhoneFormik}
        handleCkycFieldChange={handleCkycFieldChange}
        handleCkycValidate={handleCkycValidate}
      />
    </>
  );
}

export default IdentityDetailsScreen;
